import {useRef, useState, useEffect, lazy, Suspense} from 'react'

import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'

import {isFeatureEnabled} from '@github-ui/feature-flags'

import {ContentfulLogoSuite} from '@github-ui/swp-core/components/contentful/ContentfulLogoSuite'
import type {PrimerComponentLogoSuite} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentLogoSuite'

import type {GenericSectionWithIds, GenericContent, GenericGroup} from '../../../../brand/lib/types/contentful'

import {useDomReady} from '../../../../lib/utils/platform'
import {Spacer} from './../../_components/Spacer/Spacer'
import HeroCarousel from '../../_components/HeroCarousel/HeroCarousel'
import HeroCarouselControls from '../../_components/HeroCarousel/HeroCarouselControls/HeroCarouselControls'

import type Assets from './../../webgl-utils/assets'

import {HeroContent} from './HeroContent'
const IntroHeroUniverse = lazy(() => import('../../IntroHeroUniverse'))
const IntroHero = lazy(() => import('../../IntroHero'))

type Props = {
  contentfulContent: GenericSectionWithIds
  assetsRef: React.RefObject<Assets | null>
}

export function HeroSection(props: Props) {
  const {contentfulContent} = props

  const isUniverseAnimationsEnabled = isFeatureEnabled('site_homepage_universe_animations')
  const [isAssetsLoaded, setIsAssetsLoaded] = useState<boolean>(false)
  const {heroContent, heroCarousel} = contentfulContent.ids as {
    heroContent: GenericContent
    heroCarousel: GenericGroup
  }

  const logosComponent = contentfulContent.fields.content?.find(
    item => item.sys.contentType.sys.id === 'primerComponentLogoSuite',
  ) as PrimerComponentLogoSuite | undefined

  const [currentCarouselScreenIndex, setCurrentCarouselScreenIndex] = useState<number>(0)

  const heroRef = useRef<HTMLDivElement | null>(null)
  const isDomReady = useDomReady()
  const setCopyScrollOpacity = (copyScrollOpacity: number) => {
    if (heroRef.current) {
      heroRef.current.style.opacity = `${copyScrollOpacity}` // Set the desired opacity value
    }
  }

  const setCopyScrollScale = (copyScrollScale: number) => {
    if (heroRef.current) {
      heroRef.current.style.transform = `scale(${copyScrollScale})` // Set the desired scale value
    }
  }

  const heroCarouselItems = heroCarousel?.fields.content ? (heroCarousel.fields.content as GenericContent[]) : undefined

  useEffect(() => {
    const assets = props.assetsRef.current

    if (!assets) return
    let isClosed = false
    const callback = () => {
      if (isClosed) return
      setIsAssetsLoaded(true)
    }

    if (assets.isLoaded) {
      callback()
    } else {
      assets.addCallback(callback)
    }

    return () => {
      isClosed = true
    }
  }, [props.assetsRef])

  return (
    <section id="hero" className={isUniverseAnimationsEnabled ? 'hero-universe' : ''}>
      <div className="lp-Intro">
        {/* Header 72 + Global Banner 42 = 114px */}
        <div className="lp-Intro-gradient" />

        <Spacer size="114px" />

        <Spacer className="lp-IntroTop-spacer" size="0" size768="20px" size1012="50px" />

        {isDomReady && (
          <Suspense fallback={null}>
            {props.assetsRef &&
              (isUniverseAnimationsEnabled ? (
                <IntroHeroUniverse
                  setCopyScrollOpacity={setCopyScrollOpacity}
                  setCopyScrollScale={setCopyScrollScale}
                  isAssetsLoaded={isAssetsLoaded}
                />
              ) : (
                <IntroHero
                  setCopyScrollOpacity={setCopyScrollOpacity}
                  setCopyScrollScale={setCopyScrollScale}
                  assetsRef={props.assetsRef}
                />
              ))}
          </Suspense>
        )}

        {heroContent ? <HeroContent ref={heroRef} contentfulContent={heroContent} /> : null}

        <Spacer className="lp-IntroVisuals-spacer" size="64px" size768="600px" size1012="660px" />

        <HeroCarousel
          currentScreenIndex={currentCarouselScreenIndex}
          onAutoPlayTimeout={setCurrentCarouselScreenIndex}
          carouselAriaLabel="GitHub features"
        />
      </div>

      {heroCarouselItems ? (
        <HeroCarouselControls
          items={heroCarouselItems.map(item => ({
            label: item.fields.heading || '',
            value: item.fields.id || '',
            description: item.fields.subheading ? documentToPlainTextString(item.fields.subheading) : '',
          }))}
          currentIndex={currentCarouselScreenIndex}
          onChange={setCurrentCarouselScreenIndex}
          carouselDescriptionId="heroCarouselDescription"
        />
      ) : null}

      {logosComponent ? <ContentfulLogoSuite component={logosComponent} className="tmp-pb-7 Logos-logoSuite" /> : null}
    </section>
  )
}
